import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import CodeInput from '../components/CodeInput';
import Countdown from '../components/clientVerification/Countdown';
import VerificationHistory from '../components/clientVerification/VerificationHistory';
import { formatDate } from '../utils/dateUtils';
import clientVerificationService from '../services/clientVerificationService';
import { parseVerificationError } from '../utils/verificationErrors';

const CODE_LENGTH = 6;

// Events, not rows. The history is folded into one row per verification, and a
// busy session runs to about six events, so this is roughly 25 rows' worth. The
// API caps the parameter at 200.
const HISTORY_EVENT_LIMIT = 150;

// Matches the backend's MIN_QUERY_LENGTH; shorter queries are a 422 there.
const MIN_QUERY_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 350;

const CHANNEL_LABELS = {
  phone: { text: 'Text message', icon: 'bi-phone' },
  email: { text: 'Email', icon: 'bi-envelope' }
};

const ClientVerification = () => {
  const [error, setError] = useState('');

  // --- client search ---
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [usingLocalClients, setUsingLocalClients] = useState(false);
  // Contacts the CRM matched but that cannot be verified. Shown rather than
  // dropped: an incomplete CRM record and a client who is not in the directory at
  // all otherwise produce the same empty list, and only one of them is fixable.
  const [unverifiableNote, setUnverifiableNote] = useState('');

  const [selectedContact, setSelectedContact] = useState(null);
  const [channel, setChannel] = useState('');
  const [reason, setReason] = useState('');

  const [verification, setVerification] = useState(null);
  const [sending, setSending] = useState(false);
  // Set only by Countdown's onExpire. It used to be derived from a per-second
  // secondsLeft, which flashed "expired" on every send: resetVerificationState
  // zeroed the counter before the await, so the first render with a live code
  // showed the boxes disabled and the expiry notice until the timer's first
  // tick repainted.
  const [expired, setExpired] = useState(false);

  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [attemptError, setAttemptError] = useState('');
  const [attemptsRemaining, setAttemptsRemaining] = useState(null);
  const [locked, setLocked] = useState(false);
  const [result, setResult] = useState(null);

  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);

  // Guards the auto-submit so a re-render cannot fire a second verify for the
  // same code, which would burn a second attempt.
  const submittedCodeRef = useRef('');
  // Guards against a slow history fetch for a previously selected client
  // landing after a newer one.
  const eventsRequestRef = useRef(0);
  // Same guard for the directory search: a slow response for an earlier query
  // must not replace the results for what the advisor has since typed.
  const searchRequestRef = useRef(0);
  // Picking a result writes the contact's name into the box, which would
  // otherwise immediately trigger a fresh search for that name.
  const justPickedRef = useRef(false);

  // Debounced directory search. The pattern (350ms, minimum two characters,
  // request-sequence guard) matches CheckinAdmin's picker; the directory is large,
  // so it is searched server-side rather than filtered in the browser.
  useEffect(() => {
    if (justPickedRef.current) {
      justPickedRef.current = false;
      return undefined;
    }

    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setShowResults(false);
      setSearchError('');
      setUnverifiableNote('');
      return undefined;
    }

    let active = true;
    const requestId = ++searchRequestRef.current;

    const timer = setTimeout(() => {
      setSearching(true);
      setShowResults(true);
      setSearchError('');

      clientVerificationService.searchClients(trimmed)
        .then((data) => {
          if (!active || requestId !== searchRequestRef.current) return;
          setResults(data.clients);
          setUsingLocalClients(data.source === 'local');
          setUnverifiableNote(data.unverifiableNote || '');
        })
        .catch((err) => {
          if (!active || requestId !== searchRequestRef.current) return;
          setResults([]);
          setUnverifiableNote('');
          const parsed = parseVerificationError(err);
          // A directory failure must never read as "no such client" - that is
          // what would send an advisor off to verify some weaker way. The two
          // causes are kept apart because only one of them is worth retrying.
          if (parsed.errorCode === 'directory_not_synced') {
            setSearchError(
              'The client directory has not been populated yet, so no one can be ' +
              'found. It refreshes on a schedule - this needs looking at rather ' +
              'than retrying.'
            );
          } else if (parsed.errorCode === 'directory_unavailable') {
            setSearchError(
              'The client directory could not be read, so this search is ' +
              'incomplete. Try again in a moment.'
            );
          } else {
            setSearchError(`Could not search the client directory: ${parsed.message}`);
          }
        })
        .finally(() => {
          if (active && requestId === searchRequestRef.current) setSearching(false);
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [query]);

  // Only ever called when the selected client changes, so the table genuinely is
  // being replaced and a spinner is honest. Every other update merges the audit
  // record the mutation returned - see applyAuditEvent.
  const loadEvents = useCallback(async (contactId) => {
    const seq = ++eventsRequestRef.current;
    if (!contactId) {
      setEvents([]);
      return;
    }
    setEventsLoading(true);
    try {
      const data = await clientVerificationService.fetchEvents({
        clientId: contactId, limit: HISTORY_EVENT_LIMIT
      });
      // Selecting A then B quickly must not paint A's history under B's name.
      if (seq !== eventsRequestRef.current) return;
      setEvents(data.events || []);
    } catch (err) {
      console.error('Failed to load verification history', err);
      if (seq === eventsRequestRef.current) setEvents([]);
    } finally {
      if (seq === eventsRequestRef.current) setEventsLoading(false);
    }
  }, []);

  /**
   * Add the audit record a mutation just wrote.
   *
   * A null event is not a failure to handle: it means the API wrote no row -
   * a reused in-flight code writes none, and audit writes are best-effort. In
   * both cases the log genuinely has nothing new, so refetching would return
   * the identical rows and inventing one would show an event that does not
   * exist in the compliance record.
   *
   * Still stored flat, one entry per event. VerificationHistory folds them into
   * rows at render time, so a new event joins its existing session without this
   * needing to know which row it belongs to.
   */
  const applyAuditEvent = useCallback((event) => {
    if (!event) return;
    setEvents((prev) => (
      prev.some((e) => e.event_id === event.event_id)
        ? prev
        : [event, ...prev].slice(0, HISTORY_EVENT_LIMIT)
    ));
  }, []);

  const resetVerificationState = useCallback(() => {
    setVerification(null);
    setExpired(false);
    setCode('');
    setAttemptError('');
    setAttemptsRemaining(null);
    setLocked(false);
    setResult(null);
    submittedCodeRef.current = '';
  }, []);

  const handleSelectContact = useCallback((contact) => {
    justPickedRef.current = true;
    // Cancels any search still in flight, so a late response cannot reopen the
    // list over the selection just made.
    searchRequestRef.current += 1;

    setQuery(contact.display_name || '');
    setShowResults(false);
    setSearchError('');
    setSelectedContact(contact);
    setReason('');
    setError('');
    resetVerificationState();

    // Phone is preselected when there is one. Email is deliberately not
    // auto-selected in its absence: it is the weaker factor, and picking it
    // should be a decision the advisor makes rather than a default they inherit
    // from a gap in the CRM record.
    const destinations = contact.destinations || [];
    const phone = destinations.find((d) => d.channel === 'phone');
    setChannel(phone ? 'phone' : '');

    loadEvents(contact.contact_id);
  }, [loadEvents, resetVerificationState]);

  const clearSelection = useCallback(() => {
    setSelectedContact(null);
    setChannel('');
    setQuery('');
    setResults([]);
    setShowResults(false);
    setReason('');
    setError('');
    setEvents([]);
    eventsRequestRef.current += 1;
    resetVerificationState();
  }, [resetVerificationState]);

  const handleQueryChange = useCallback((value) => {
    setQuery(value);
    if (!selectedContact) return;

    setSelectedContact(null);
    setChannel('');
    setReason('');
    setError('');
    setEvents([]);
    setResults([]);
    setShowResults(false);
    setSearchError('');
    setUnverifiableNote('');
    setUsingLocalClients(false);
    eventsRequestRef.current += 1;
    resetVerificationState();
  }, [resetVerificationState, selectedContact]);

  const destinations = useMemo(
    () => selectedContact?.destinations || [], [selectedContact]
  );

  const selectedDestination = useMemo(
    () => destinations.find((d) => d.channel === channel) || null,
    [destinations, channel]
  );

  const handleExpire = useCallback(() => setExpired(true), []);

  const handleSendCode = async () => {
    if (!selectedContact || !selectedDestination) return;
    setSending(true);
    setError('');
    resetVerificationState();
    try {
      const data = await clientVerificationService.sendCode({
        contactId: selectedContact.contact_id,
        channel,
        displayName: selectedContact.display_name || '',
        // What is on screen. The service re-resolves the real destination and
        // refuses the send if the CRM no longer agrees.
        destinationMasked: selectedDestination.masked,
        reason: reason.trim() || null
      });
      setVerification(data);
      setAttemptsRemaining(data.attempts_remaining);
      applyAuditEvent(data.audit_event);
    } catch (err) {
      const parsed = parseVerificationError(err);
      if (parsed.errorCode === 'rate_limited' && parsed.retryAfterSeconds) {
        const mins = Math.ceil(parsed.retryAfterSeconds / 60);
        setError(`${parsed.message} Try again in about ${mins} minute${mins === 1 ? '' : 's'}.`);
      } else if (parsed.errorCode === 'destination_changed') {
        setError(
          'This client\'s details have changed since you chose them, so ' +
          'nothing was sent. Search for them again and confirm where it should go.'
        );
      } else if (parsed.errorCode === 'directory_unavailable'
                 || parsed.errorCode === 'directory_not_synced') {
        setError(
          'The client directory could not be read, so the destination could not ' +
          'be confirmed and nothing was sent.'
        );
      } else {
        setError(parsed.message);
      }
      applyAuditEvent(parsed.auditEvent);
    } finally {
      setSending(false);
    }
  };

  const handleCodeChange = useCallback((next) => {
    setCode(next);
    // Drop the rejected-entry styling as soon as a new entry begins. Bootstrap's
    // is-invalid icon sits on top of a 50px centred box, so leaving it up while
    // the advisor retypes hides the character they just entered. The attempts
    // counter below the boxes carries the information onward in a muted form.
    setAttemptError('');
  }, []);

  const handleVerify = useCallback(async (submitted) => {
    if (!verification || verifying || locked) return;
    if (submittedCodeRef.current === submitted) return;
    submittedCodeRef.current = submitted;

    setVerifying(true);
    setAttemptError('');
    try {
      const data = await clientVerificationService.verifyCode({
        verificationId: verification.verification_id,
        code: submitted
      });
      setResult(data);
      setCode('');
      applyAuditEvent(data.audit_event);
    } catch (err) {
      const parsed = parseVerificationError(err);
      if (parsed.status === 422 || parsed.errorCode === 'malformed_code') {
        // Not reachable from this page: CodeInput admits only A-Z and 0-9, and
        // only submits at exactly six characters, which is precisely what
        // CODE_PATTERN accepts. Kept as a guard in case the two ever drift --
        // if this message appears, the boxes are accepting something the API
        // will not, and the entry is silently costing no attempt.
        setAttemptError('That entry was not a valid six-character code. Please re-enter it.');
      } else if (parsed.errorCode === 'invalid_code') {
        const left = parsed.attemptsRemaining;
        setAttemptsRemaining(left);
        setAttemptError(
          left === 1
            ? 'Incorrect. 1 attempt remaining before this code is cancelled.'
            : `Incorrect. ${left} attempts remaining.`
        );
      } else if (parsed.errorCode === 'attempts_exceeded') {
        setLocked(true);
        setAttemptsRemaining(0);
        setAttemptError('Too many incorrect attempts. This code has been cancelled.');
      } else if (parsed.errorCode === 'expired' || parsed.errorCode === 'not_found') {
        setLocked(true);
        setAttemptError('This code is no longer valid. Send a new one.');
      } else {
        setAttemptError(parsed.message);
      }
      setCode('');
      submittedCodeRef.current = '';
      applyAuditEvent(parsed.auditEvent);
    } finally {
      setVerifying(false);
    }
  }, [verification, verifying, locked, applyAuditEvent]);

  const handleCancel = async () => {
    if (!verification) return;
    try {
      const data = await clientVerificationService.cancelCode(verification.verification_id);
      resetVerificationState();
      applyAuditEvent(data?.audit_event);
    } catch (err) {
      setError(`Failed to cancel the code: ${parseVerificationError(err).message}`);
    }
  };

  const canSend = Boolean(selectedContact && selectedDestination && !sending);

  return (
    <div className="container mt-4">
      <div className="d-flex align-items-center mb-1">
        <i className="bi bi-shield-check fs-3 me-2 text-primary"></i>
        <h2 className="mb-0">Client Verification</h2>
      </div>
      <p className="text-muted">
        Send a one-time code to the mobile or email on file, then have the client read it
        back to you on the call. The code is never shown here on purpose. Contact details
        come from a directory refreshed once a day, so a change made in the CRM today
        appears here tomorrow.
      </p>

      {usingLocalClients && (
        <div className="alert alert-warning d-flex align-items-center" role="alert">
          <i className="bi bi-tools me-2"></i>
          <div>
            <strong>Local test mode.</strong> These are test contacts from the verification
            service&apos;s local directory, not real client records. Codes are written to
            the service&apos;s <code>data/outbox.json</code> rather than sent.
          </div>
        </div>
      )}

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')}></button>
        </div>
      )}

      <div className="row">
        <div className="col-lg-6">
          <div className="card mb-4">
            <div className="card-header">
              <strong>1. Choose the client</strong>
            </div>
            <div className="card-body">
              <label className="form-label" htmlFor="clientSearch">
                Search clients
              </label>
              <div className="input-group">
                <span className="input-group-text"><i className="bi bi-search"></i></span>
                <input
                  id="clientSearch"
                  type="text"
                  className="form-control"
                  placeholder="Search by name..."
                  value={query}
                  onChange={(e) => handleQueryChange(e.target.value)}
                  disabled={sending || verifying}
                  autoComplete="off"
                />
                {selectedContact && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={clearSelection}
                    disabled={sending || verifying}
                    aria-label="Clear the selected client"
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                )}
              </div>

              {searching && (
                <div className="mt-2 text-muted small">
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Searching the client directory...
                </div>
              )}

              {searchError && (
                <div className="alert alert-warning py-2 mt-2 mb-0">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {searchError}
                </div>
              )}

              {showResults && !searching && !searchError && results.length === 0 && (
                <div className="mt-2 text-muted small">
                  {unverifiableNote
                    ? 'No contacts match that name and can be verified.'
                    : 'No contacts match that name.'}
                </div>
              )}

              {/* Shown whether or not there are results. With an empty list it is
                  the whole explanation; alongside results it accounts for the
                  contact the advisor expected to see and cannot. */}
              {showResults && !searching && !searchError && unverifiableNote && (
                <div className="alert alert-secondary py-2 mt-2 mb-0 small">
                  <i className="bi bi-info-circle me-2"></i>
                  {unverifiableNote}
                  <div className="text-muted mt-1">
                    Their CRM record needs a contact ID and a mobile number or email
                    address before a code can be sent.
                  </div>
                </div>
              )}

              {showResults && results.length > 0 && (
                <div className="list-group mt-2">
                  {results.map((contact) => (
                    <button
                      type="button"
                      key={contact.contact_id}
                      className="list-group-item list-group-item-action"
                      onClick={() => handleSelectContact(contact)}
                    >
                      <div className="fw-semibold">{contact.display_name}</div>
                      {contact.household_name && (
                        <div className="text-muted small">{contact.household_name}</div>
                      )}
                      <div className="small mt-1">
                        {(contact.destinations || []).length === 0 ? (
                          <span className="text-danger">
                            <i className="bi bi-exclamation-triangle me-1"></i>
                            No phone or email on file
                          </span>
                        ) : (
                          contact.destinations.map((d) => (
                            <span key={d.channel} className="badge bg-light text-dark me-1">
                              <i className={`bi ${CHANNEL_LABELS[d.channel]?.icon} me-1`}></i>
                              {d.masked}
                            </span>
                          ))
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {selectedContact && (
                <div className="mt-3">
                  {destinations.length === 0 ? (
                    <div className="alert alert-warning mb-0">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      There is no mobile number or email address on file for
                      {' '}{selectedContact.display_name}. Verify another way.
                    </div>
                  ) : (
                    <>
                      <label className="form-label fw-semibold">Send the code by</label>
                      {destinations.map((destination) => (
                        <div className="form-check" key={destination.channel}>
                          <input
                            className="form-check-input"
                            type="radio"
                            name="verificationChannel"
                            id={`channel_${destination.channel}`}
                            checked={channel === destination.channel}
                            onChange={() => setChannel(destination.channel)}
                            disabled={Boolean(verification) || sending}
                          />
                          <label
                            className="form-check-label"
                            htmlFor={`channel_${destination.channel}`}
                          >
                            <i
                              className={`bi ${CHANNEL_LABELS[destination.channel]?.icon} me-2`}
                            ></i>
                            {CHANNEL_LABELS[destination.channel]?.text || destination.channel}
                            <span className="font-monospace ms-2">{destination.masked}</span>
                          </label>
                        </div>
                      ))}

                      {channel === 'email' && (
                        <div className="text-muted small mt-2">
                          <i className="bi bi-info-circle me-1"></i>
                          Email is a weaker check than a text: it may be open on the same
                          device the client is calling from. The channel is recorded.
                        </div>
                      )}

                      <div className="mt-3">
                        <label className="form-label" htmlFor="verificationReason">
                          What is this for? <span className="text-muted">(optional, recorded)</span>
                        </label>
                        <input
                          id="verificationReason"
                          type="text"
                          className="form-control"
                          maxLength={200}
                          placeholder="e.g. cash request"
                          value={reason}
                          onChange={(e) => setReason(e.target.value)}
                          disabled={Boolean(verification) || sending}
                        />
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-lg-6">
          <div className="card mb-4">
            <div className="card-header">
              <strong>2. Send and confirm</strong>
            </div>
            <div className="card-body">
              {!verification && (
                <>
                  <p className="text-muted">
                    The client will receive a {CODE_LENGTH}-character code and read it back to you.
                  </p>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSendCode}
                    disabled={!canSend}
                  >
                    {sending && <span className="spinner-border spinner-border-sm me-2"></span>}
                    <i className="bi bi-send me-2"></i>
                    Send code
                  </button>
                </>
              )}

              {verification && !result && (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <div className="text-muted small">
                        Sent by {CHANNEL_LABELS[verification.channel]?.text?.toLowerCase()
                          || verification.channel} to
                      </div>
                      <div className="fs-5 font-monospace">
                        {verification.destination_masked}
                      </div>
                    </div>
                    <Countdown
                      expiresAt={verification.expires_at}
                      onExpire={handleExpire}
                    />
                  </div>

                  {verification.reused_existing && (
                    <div className="alert alert-info py-2">
                      A code was already on its way to this client, so we did not send another.
                    </div>
                  )}

                  {expired && (
                    <div className="alert alert-secondary py-2">
                      This code has expired. Send a new one.
                    </div>
                  )}

                  <label className="form-label">Enter the code the client reads back</label>
                  <CodeInput
                    length={CODE_LENGTH}
                    value={code}
                    onChange={handleCodeChange}
                    onComplete={handleVerify}
                    disabled={verifying || locked || expired}
                    invalid={Boolean(attemptError)}
                    autoFocus
                  />

                  {verifying && (
                    <div className="text-center text-muted mt-2">
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Checking...
                    </div>
                  )}

                  {attemptError && (
                    <div className="alert alert-danger mt-3 py-2 mb-0">{attemptError}</div>
                  )}

                  {!attemptError && attemptsRemaining !== null && !locked && (
                    <div className="text-muted small text-center mt-2">
                      {attemptsRemaining} attempt{attemptsRemaining === 1 ? '' : 's'} remaining
                    </div>
                  )}

                  <div className="d-flex gap-2 mt-3">
                    {(locked || expired) && (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleSendCode}
                        disabled={sending}
                      >
                        {sending && <span className="spinner-border spinner-border-sm me-2"></span>}
                        Send a new code
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={handleCancel}
                      disabled={sending}
                    >
                      Cancel code
                    </button>
                  </div>
                </>
              )}

              {result && (
                <div className="text-center py-3">
                  <i className="bi bi-shield-check text-success" style={{ fontSize: '3rem' }}></i>
                  <h4 className="text-success mt-2 mb-1">Verified</h4>
                  <p className="mb-1">
                    {result.client_name} confirmed the code at {formatDate(result.verified_at)}.
                  </p>
                  <p className="text-muted small mb-3">
                    The code has been deleted. This verification is recorded in the history below.
                  </p>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={resetVerificationState}
                  >
                    Done
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <VerificationHistory
        clientName={selectedContact?.display_name || ''}
        events={events}
        loading={eventsLoading}
      />
    </div>
  );
};

export default ClientVerification;
